import React, { FC, useEffect, useState } from 'react';

import usePrefix from 'utils/usePrefix';
import { ResponseError } from 'utils/api';
import { FavoriteSchool } from 'utils/api/inbox';
import { interventionPlansApi } from 'utils/api/interventionPlans';
import { notificationTypes } from 'utils/constants';

import { useApp } from 'App';
import { ActionTypes } from 'App/types';

import Icon from 'components/Icons';

import FavoriteSchoolsModal from './FavoriteSchoolsModal';
import { Star } from './styles';

interface Props {
  planId: number;
}

const FavoriteStar: FC<Props> = ({ planId }) => {
  const t = usePrefix('InterventionPlans');
  const [, dispatch] = useApp();
  const [schools, setSchools] = useState<FavoriteSchool[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const dispatchError = (error: unknown) =>
    dispatch({
      type: ActionTypes.SET_NOTIFICATION_CODE,
      payload: { code: (error as ResponseError)?.parsed?.code, type: notificationTypes.error },
    });

  useEffect(() => {
    let active = true;
    setSchools(null);
    interventionPlansApi
      .getFavorites(planId)
      .then((data) => active && setSchools(data))
      .catch((error) => {
        if (!active) return;
        dispatchError(error);
        setSchools([]);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  if (!schools || schools.length === 0) return null;

  const isFavorite = schools.some((school) => school.isFavorite);

  const persist = async (schoolIds: number[]): Promise<boolean> => {
    setSaving(true);
    try {
      setSchools(await interventionPlansApi.setFavorites(planId, schoolIds));
      return true;
    } catch (error) {
      dispatchError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => {
    if (saving) return;
    if (schools.length === 1) {
      const [only] = schools;
      persist(only.isFavorite ? [] : [only.schoolId]);
    } else {
      setModalOpen(true);
    }
  };

  const handleSave = async (schoolIds: number[]) => {
    if (await persist(schoolIds)) setModalOpen(false);
  };

  return (
    <>
      <Star
        type="button"
        onClick={handleClick}
        title={isFavorite ? t('favorite_remove') : t('favorite_add')}
        aria-label={isFavorite ? t('favorite_remove') : t('favorite_add')}
      >
        <Icon type={isFavorite ? 'starFill' : 'star'} />
      </Star>
      {modalOpen && (
        <FavoriteSchoolsModal
          isOpen={modalOpen}
          schools={schools}
          saving={saving}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default FavoriteStar;
